import React, { useState, useEffect, useCallback } from 'react';
import {
  FaCog,
  FaSearch,
  FaSave,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaBullhorn,
  FaToggleOn,
  FaToggleOff,
  FaLink,
  FaCopy,
  FaCrown
} from 'react-icons/fa';
import { getSystemConfig, updateSystemConfig, generateLifetimeMembershipCode, getLifetimeMembershipCodes } from '../api/client';

const System = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editValues, setEditValues] = useState({});

  // Lifetime membership code state
  const [lifetimeCodes, setLifetimeCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [codeFilter, setCodeFilter] = useState('unused'); // 'all', 'unused', 'used'

  const loadLifetimeCodes = useCallback(async () => {
    setLoadingCodes(true);
    try {
      const result = await getLifetimeMembershipCodes('all');
      setLifetimeCodes(result.codes || []);
    } catch (err) {
      console.error('Error loading lifetime codes:', err);
    } finally {
      setLoadingCodes(false);
    }
  }, []);

  // Filter codes based on selected filter
  const filteredCodes = lifetimeCodes.filter(code => {
    if (codeFilter === 'unused') return !code.is_used;
    if (codeFilter === 'used') return code.is_used;
    return true; // 'all'
  });

  const unusedCount = lifetimeCodes.filter(c => !c.is_used).length;
  const usedCount = lifetimeCodes.filter(c => c.is_used).length;

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getSystemConfig();
      setConfig(result);
      // Initialize edit values
      const values = {};
      Object.keys(result).forEach(key => {
        values[key] = result[key].value;
      });
      setEditValues(values);
    } catch (err) {
      console.error('Error loading system config:', err);
      if (err.response?.status === 403) {
        setError('You do not have access to system configuration.');
      } else {
        setError('Failed to load system configuration.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadLifetimeCodes();
  }, [loadConfig, loadLifetimeCodes]);

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    setError('');
    setSuccess('');
    try {
      await updateSystemConfig(key, editValues[key]);
      setSuccess(`${key} updated successfully.`);
      // Reload config to get updated timestamp
      await loadConfig();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(`Failed to save ${key}.`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveMaintenanceSettings = async () => {
    const keys = ['maintenance_message', 'maintenance_scheduled_at', 'maintenance_downtime_hours', 'maintenance_active'];
    setSaving(prev => ({ ...prev, maintenance: true }));
    setError('');
    setSuccess('');
    try {
      for (const key of keys) {
        await updateSystemConfig(key, editValues[key] || '');
      }
      setSuccess('Maintenance settings updated successfully.');
      await loadConfig();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving maintenance settings:', err);
      setError('Failed to save maintenance settings.');
    } finally {
      setSaving(prev => ({ ...prev, maintenance: false }));
    }
  };

  const handleValueChange = (key, value) => {
    setEditValues(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerateLifetimeCode = async () => {
    setGeneratingCode(true);
    setError('');
    try {
      const result = await generateLifetimeMembershipCode();
      if (result.status === 'success') {
        setSuccess(`Lifetime membership code generated: ${result.code}`);
        await loadLifetimeCodes();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Error generating lifetime code:', err);
      setError('Failed to generate lifetime membership code.');
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyUrl = async (url, code) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary-500" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <FaCog className="text-white text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-800">System Configuration</h1>
            <p className="text-neutral-600">Manage system-wide settings</p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <FaCheckCircle className="text-green-500" />
          <span className="text-green-700">{success}</span>
        </div>
      )}
      {error && config && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FaExclamationTriangle className="text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Configuration Cards */}
      <div className="space-y-6">
        {/* Search Radius */}
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <FaSearch className="text-white text-xl" />
              <h2 className="text-lg font-semibold text-white">Exploration Settings</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Search Radius (miles/kilometers)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  min="1"
                  max="50"
                  step="1"
                  value={editValues.search_radius || ''}
                  onChange={(e) => handleValueChange('search_radius', e.target.value)}
                  className="w-32 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => handleSave('search_radius')}
                  disabled={saving.search_radius || editValues.search_radius === config?.search_radius?.value}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    saving.search_radius || editValues.search_radius === config?.search_radius?.value
                      ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {saving.search_radius ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Save
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-sm text-neutral-500 flex items-start gap-2">
                <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                <span>
                  This radius is used when exploring places between two points in an itinerary.
                  The search will find attractions within this distance from the route.
                </span>
              </p>
              {config?.search_radius?.updated_at && (
                <p className="mt-2 text-xs text-neutral-400">
                  Last updated: {formatDate(config.search_radius.updated_at)}
                </p>
              )}
            </div>

            {/* Advisory Radius */}
            <div className="mt-6 pt-6 border-t border-neutral-200">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Advisory Radius (miles)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  min="5"
                  max="200"
                  step="5"
                  value={editValues.advisory_radius || ''}
                  onChange={(e) => handleValueChange('advisory_radius', e.target.value)}
                  className="w-32 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => handleSave('advisory_radius')}
                  disabled={saving.advisory_radius || editValues.advisory_radius === config?.advisory_radius?.value}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    saving.advisory_radius || editValues.advisory_radius === config?.advisory_radius?.value
                      ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {saving.advisory_radius ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Save
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-sm text-neutral-500 flex items-start gap-2">
                <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                <span>
                  This radius defines how far around the destination to check for travel advisories.
                  Larger values will include advisories for a wider surrounding area.
                </span>
              </p>
              {config?.advisory_radius?.updated_at && (
                <p className="mt-2 text-xs text-neutral-400">
                  Last updated: {formatDate(config.advisory_radius.updated_at)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* System Maintenance Settings */}
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <FaBullhorn className="text-white text-xl" />
              <h2 className="text-lg font-semibold text-white">System Maintenance Settings</h2>
            </div>
          </div>
          <div className="p-6 space-y-5">
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
              <div>
                <label className="block text-sm font-medium text-neutral-700">Broadcast Active</label>
                <p className="text-xs text-neutral-500 mt-0.5">Enable to show the maintenance banner to all users</p>
              </div>
              <button
                type="button"
                onClick={() => handleValueChange('maintenance_active', editValues.maintenance_active === 'true' ? 'false' : 'true')}
                className="text-3xl transition-colors"
              >
                {editValues.maintenance_active === 'true' ? (
                  <FaToggleOn className="text-amber-500" />
                ) : (
                  <FaToggleOff className="text-neutral-400" />
                )}
              </button>
            </div>

            {/* Broadcast Message */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Broadcast Message
              </label>
              <textarea
                rows={3}
                value={editValues.maintenance_message || ''}
                onChange={(e) => handleValueChange('maintenance_message', e.target.value)}
                placeholder="e.g. Scheduled maintenance: The system will be temporarily unavailable for upgrades."
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
              />
            </div>

            {/* Scheduled Date/Time & Downtime Hours */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Scheduled Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={editValues.maintenance_scheduled_at || ''}
                  onChange={(e) => handleValueChange('maintenance_scheduled_at', e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Expected Downtime (hours)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editValues.maintenance_downtime_hours || ''}
                  onChange={(e) => handleValueChange('maintenance_downtime_hours', e.target.value)}
                  placeholder="e.g. 2"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>

            {/* Preview */}
            {editValues.maintenance_active === 'true' && editValues.maintenance_message && (
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Banner Preview</p>
                <p className="text-sm text-amber-800 font-medium">{editValues.maintenance_message}</p>
                {editValues.maintenance_scheduled_at && (
                  <p className="text-xs text-amber-600 mt-1">
                    Scheduled: {new Date(editValues.maintenance_scheduled_at).toLocaleString()}
                    {editValues.maintenance_downtime_hours ? ` | Downtime: ${editValues.maintenance_downtime_hours}h` : ''}
                  </p>
                )}
              </div>
            )}

            <p className="text-sm text-neutral-500 flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 flex-shrink-0" />
              <span>
                When active, a flashing banner will appear on the Dashboard page for all website and mobile app users.
              </span>
            </p>

            {/* Save Button */}
            <button
              onClick={handleSaveMaintenanceSettings}
              disabled={saving.maintenance}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all ${
                saving.maintenance
                  ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-md'
              }`}
            >
              {saving.maintenance ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Maintenance Settings
                </>
              )}
            </button>

            {config?.maintenance_active?.updated_at && (
              <p className="text-xs text-neutral-400">
                Last updated: {formatDate(config.maintenance_active.updated_at)}
              </p>
            )}
          </div>
        </div>

        {/* Lifetime Membership URL Generator */}
        <div className="bg-white rounded-xl shadow-md border border-neutral-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <FaCrown className="text-white text-xl" />
              <h2 className="text-lg font-semibold text-white">Generate Lifetime Membership URL</h2>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm text-neutral-600 mb-4">
              Generate one-time activation URLs for lifetime Navigator memberships. Each code can only be used once during registration.
            </p>

            {/* Generate Button */}
            <button
              onClick={handleGenerateLifetimeCode}
              disabled={generatingCode}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all mb-6 ${
                generatingCode
                  ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-md'
              }`}
            >
              {generatingCode ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaLink />
                  Generate Lifetime Membership URL
                </>
              )}
            </button>

            {/* List of Generated Codes */}
            <div className="border-t border-neutral-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-neutral-700">Generated Codes</h3>
                {lifetimeCodes.length > 0 && (
                  <div className="flex gap-1 text-xs">
                    <button
                      onClick={() => setCodeFilter('unused')}
                      className={`px-3 py-1 rounded-full transition-colors ${
                        codeFilter === 'unused'
                          ? 'bg-purple-500 text-white'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                      }`}
                    >
                      Available ({unusedCount})
                    </button>
                    <button
                      onClick={() => setCodeFilter('used')}
                      className={`px-3 py-1 rounded-full transition-colors ${
                        codeFilter === 'used'
                          ? 'bg-purple-500 text-white'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                      }`}
                    >
                      Used ({usedCount})
                    </button>
                    <button
                      onClick={() => setCodeFilter('all')}
                      className={`px-3 py-1 rounded-full transition-colors ${
                        codeFilter === 'all'
                          ? 'bg-purple-500 text-white'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                      }`}
                    >
                      All
                    </button>
                  </div>
                )}
              </div>
              {loadingCodes ? (
                <div className="flex items-center justify-center py-4">
                  <FaSpinner className="animate-spin text-purple-500" />
                </div>
              ) : lifetimeCodes.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">No lifetime membership codes have been generated yet.</p>
              ) : filteredCodes.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">
                  {codeFilter === 'unused' ? 'No available codes.' : codeFilter === 'used' ? 'No used codes yet.' : 'No codes found.'}
                </p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredCodes.map((codeItem) => (
                    <div
                      key={codeItem.id}
                      className={`p-4 rounded-lg border ${
                        codeItem.is_used
                          ? 'bg-neutral-50 border-neutral-200'
                          : 'bg-purple-50 border-purple-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-sm font-mono font-semibold text-purple-700">
                              {codeItem.code}
                            </code>
                            {codeItem.is_used ? (
                              <span className="px-2 py-0.5 text-xs font-medium bg-neutral-200 text-neutral-600 rounded-full">
                                Used
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                Available
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-neutral-500 truncate">
                            {codeItem.activation_url}
                          </div>
                          <div className="text-xs text-neutral-400 mt-1">
                            Created: {formatDate(codeItem.created_at)}
                            {codeItem.is_used && codeItem.activated_by_email && (
                              <span className="ml-2">
                                | Activated by: <span className="text-neutral-600">{codeItem.activated_by_email}</span>
                              </span>
                            )}
                            {codeItem.is_used && codeItem.activated_at && (
                              <span className="ml-2">
                                | Activated: {formatDate(codeItem.activated_at)}
                              </span>
                            )}
                          </div>
                        </div>
                        {!codeItem.is_used && (
                          <button
                            onClick={() => handleCopyUrl(codeItem.activation_url, codeItem.code)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                              copiedCode === codeItem.code
                                ? 'bg-green-100 text-green-700'
                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                          >
                            {copiedCode === codeItem.code ? (
                              <>
                                <FaCheckCircle />
                                Copied!
                              </>
                            ) : (
                              <>
                                <FaCopy />
                                Copy URL
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="mt-4 text-sm text-neutral-500 flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 flex-shrink-0" />
              <span>
                Share the activation URL with the recipient. When they register using this URL, they will automatically receive a lifetime Navigator membership.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default System;
